<script setup>
import {
    ref,
    getCurrentInstance,
    computed
} from 'vue'
import {
    DriverStore
} from '../pinia/Driver.js'
import { notifyDialog, notifyDialogError } from '../composables/useDialogNotice'
import CrewColorPicker from './CrewColorPicker.vue'

const Driver = DriverStore()
const { proxy } = getCurrentInstance()

function normalizeColor(value) {
    let next = String(value || '').trim()
    if (!next) return '#FFFFFF'
    if (!next.startsWith('#')) next = `#${next}`
    return next.toUpperCase()
}

const pseudo = ref("" + Driver.persona.name)
const avatar = ref(parseInt(Driver.persona.iconIndex) || 0)
const color = ref(normalizeColor(Driver.user.nicknameColor))
const canEdit = computed(() => Driver.user.premium == '1')
const avatarIds = Array.from({ length: 73 }, (_, index) => index).filter((index) => index !== 26)
const avatarUrl = (index) => `https://cdn.nightriderz.world/images/website/icon-persona/${index}.webp`

function selectAvatar(id) {
    if (canEdit.value) avatar.value = id
}

async function save() {
    let saved = false

    if (pseudo.value != Driver.persona.name) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "UpdatePersonaName",
                parameters: [pseudo.value]
            }))
            Driver.persona.name = pseudo.value
            saved = true
        } catch (err) {
            notifyDialogError(err, 'Error updating name')
            return
        }
    }

    if (avatar.value != Driver.persona.iconIndex) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "avatar",
                parameters: [avatar.value]
            }))
            Driver.persona.iconIndex = avatar.value
            saved = true
        } catch (err) {
            notifyDialogError(err, 'Error updating avatar')
            return
        }
    }

    if (normalizeColor(color.value) != normalizeColor(Driver.user.nicknameColor)) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "UpdateColor",
                parameters: [normalizeColor(color.value)]
            }))
            Driver.user.nicknameColor = normalizeColor(color.value)
            color.value = Driver.user.nicknameColor
            saved = true
        } catch (err) {
            notifyDialogError(err, 'Error updating color')
            return
        }
    }

    if (saved) {
        notifyDialog({ type: 'success', message: 'Your driver informations have been saved.' })
    }
}
</script>

<template><div class="settings-tab-page personaForm">
    <div class="settings-tab-header">
        <div class="header-icon">
            <v-icon size="28">mdi-account-edit</v-icon>
        </div>
        <div class="header-content">
            <h3 class="page-title">Driver</h3>
            <p class="page-subtitle">Customize your driver appearance</p>
        </div>
    </div>

    <v-alert v-if="!canEdit" color="info" icon="mdi-information" class="mb-4">
        You have to be a Premium member to edit your driver.
    </v-alert>

    <v-form>
        <div class="section-list">
            <div class="section-row">
                <div class="section-body">
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-account</v-icon>
                                Driver Name
                            </div>
                            <div class="setting-description">
                                Your unique driver name (3-14 characters, alphanumeric)
                            </div>
                            <v-text-field v-model="pseudo" placeholder="Driver Name" variant="outlined" density="comfortable" hide-details :disabled="!canEdit" class="setting-input"></v-text-field>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="setting-item">
                        <div class="setting-info">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-palette</v-icon>
                                Nickname Color
                            </div>
                            <div class="setting-description">
                                Choose a custom color for your driver name
                            </div>
                        </div>
                        <div class="color-picker-wrapper">
                            <CrewColorPicker v-model="color" :disabled="!canEdit" />
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-face-man</v-icon>
                                Avatar
                            </div>
                            <div class="setting-description">
                                Choose your driver avatar
                            </div>
                            <div class="avatar-preview">
                                <img class="avatar-preview-img" :src="avatarUrl(avatar)" :alt="`Avatar ${avatar}`" width="72" height="72">
                                <div class="avatar-preview-meta">
                                    <span class="avatar-preview-title">Selected</span>
                                    <span class="avatar-preview-id">Avatar {{ avatar }}</span>
                                </div>
                            </div>
                            <div class="avatar-grid" :class="{ 'is-disabled': !canEdit }" role="listbox" aria-label="Driver avatars">
                                <button v-for="id in avatarIds" :key="id" type="button" class="avatar-item" :class="{ 'is-selected': avatar === id }" :disabled="!canEdit" :aria-selected="avatar === id" :title="`Avatar ${id}`" @click="selectAvatar(id)">
                                    <img :src="avatarUrl(id)" :alt="`Avatar ${id}`" width="56" height="56" loading="lazy">
                                    <span v-if="avatar === id" class="avatar-check"><v-icon size="14">mdi-check</v-icon></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <v-btn v-if="canEdit" class="save-btn" variant="elevated" color="primary" size="large" @click="save" block>
            <v-icon class="mr-2">mdi-content-save</v-icon>
            Save Changes
        </v-btn>
    </v-form>
</div></template>

<style scoped>
.color-picker-wrapper {
  display:flex;
  align-items:center;
  justify-content:flex-end;
  padding:4px 0}
.avatar-preview {
  display:flex;
  align-items:center;
  gap:14px;
  margin:12px 0 14px;
  padding:12px;
  border-radius:10px;
  background:var(--glass);
  border:1px solid var(--line)}
.avatar-preview-img {
  width:72px;
  height:72px;
  border-radius:12px;
  object-fit:cover;
  border:2px solid rgb(var(--v-theme-primary));
  box-shadow:0 0 0 3px rgba(var(--v-theme-primary),.18)}
.avatar-preview-meta {
  display:flex;
  flex-direction:column;
  gap:2px;
  min-width:0}
.avatar-preview-title {
  font-size:12px;
  letter-spacing:.04em;
  text-transform:uppercase;
  color:var(--faint)}
.avatar-preview-id {
  font-size:15px;
  font-weight:600;
  color:var(--ink)}
.avatar-grid {
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(56px,1fr));
  gap:8px;
  max-height:280px;
  overflow-y:auto;
  overflow-x:hidden;
  padding:10px;
  background:var(--glass);
  border-radius:10px;
  border:1px solid var(--line-strong)}
.avatar-grid.is-disabled {
  opacity:.55}
.avatar-grid::-webkit-scrollbar {
  width:6px}
.avatar-grid::-webkit-scrollbar-track {
  background:var(--glass);
  border-radius:3px}
.avatar-grid::-webkit-scrollbar-thumb {
  background:rgba(var(--v-theme-primary),.3);
  border-radius:3px}
.avatar-grid::-webkit-scrollbar-thumb:hover {
  background:rgba(var(--v-theme-primary),.5)}
.avatar-item {
  position:relative;
  display:block;
  padding:0;
  margin:0;
  border:none;
  background:transparent;
  cursor:pointer;
  border-radius:10px;
  line-height:0}
.avatar-item:disabled {
  cursor:not-allowed}
.avatar-item img {
  width:100%;
  aspect-ratio:1;
  height:auto;
  border-radius:10px;
  object-fit:cover;
  border:2px solid rgba(255,255,255,.1);
  transition:border-color .2s ease,transform .2s ease,box-shadow .2s ease}
.avatar-item:not(:disabled):hover img {
  border-color:rgba(var(--v-theme-primary),.55);
  transform:translateY(-1px)}
.avatar-item.is-selected img {
  border-color:rgb(var(--v-theme-primary));
  box-shadow:0 0 0 2px rgba(var(--v-theme-primary),.35)}
.avatar-check {
  position:absolute;
  right:4px;
  bottom:4px;
  width:18px;
  height:18px;
  border-radius:50%;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  background:rgb(var(--v-theme-primary));
  color:#fff;
  box-shadow:0 1px 4px #00000059}
</style>
